<template>
  <div class="registration" style="margin-bottom: 2em;">
    <p>BeamNG.tech software is a great solution for everyone who uses simulation in their research and development across a range of industries, including the automotive sector. With BeamNG.tech you have direct access to a wide range of features which are exclusively available in this package.</p>

    <p>We offer complimentary academic licenses to eligible users such as:</p>

    <ul>
      <li>Students currently enrolled in a course, degree, or program</li>
      <li>Educators and academic staff at universities, colleges, schools, research institutes etc. (e.g. professors, lecturers, researchers)</li>
    </ul>

    <p><b>Note:</b> Use must be strictly non-commercial (research or education).</p>

    <p>If you are applying as a company, startup, non-profit, or other non-academic user, please email <a href="mailto:licensing@beamng.gmbh">licensing@beamng.gmbh</a> to explore licensing options tailored to your needs.</p>

    <p><b>Apply with your professional/academic email address as proof of membership. In the “Application Text” field, include the following information:</b></p>

    <ol>
      <li><b>Project Overview:</b> Describe your research or academic project and goals.</li>
      <li><b>Software Usage:</b> How BeamNG.tech will be used in your work. Please list any important features or tools.</li>
      <li><b>Expected Outcomes & Impact:</b> All the results, findings, or advancements you aim to achieve.</li>
      <li><b>Purpose of Research:</b> Explain if the work is intended for publishing, open-source contribution, patenting, and/or used by third-party organizations.</li>
      <li><b>Planned Attribution:</b> How you will credit BeamNG (e.g. citations, logos, acknowledgments).</li>
      <li><b>How you heard about BeamNG.tech</b> (Optional, but appreciated!).</li>
    </ol>

    <p>If available, you may also share the links to related project pages, publications, or portfolios.</p>

    <p>You will receive a confirmation email on submission, and we will review your request within 5 business days. If approved, you’ll receive access instructions for download and installation.</p>

    <form @submit="submit" v-if="result == null">
      <hr />
      <div class="alert alert-dismissable fade show alert-danger" v-if="nameError">
        Please enter your full name.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="alert alert-dismissable fade show alert-danger" v-if="emailError">
        Please enter a valid email address.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="row">
        <div class="col">
          <div class="form-group">
            <label for="applicationName">Name:</label>
            <input type="text" class="form-control" id="applicationName" aria-describedby="nameHelp" placeholder="Enter your full name" v-model.trim="name" />
          </div>
        </div>
        <div class="col">
          <div class="form-group">
            <label for="applicationEmail">Email Address:</label>
            <input type="email" class="form-control" id="applicationEmail" aria-describedby="emailHelp" placeholder="Enter the email" v-model.trim="email" maxlength="50" />
          </div>
        </div>
      </div>
      <div class="alert alert-dismissable fade show alert-danger" v-if="organizationError">
        Please enter an organization name.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="form-group">
        <label for="applicationOrganization">Organization name:</label>
        <input type="text" class="form-control" id="applicationOrganization" aria-describedby="organizationHelp" placeholder="Enter the organization name" v-model.trim="organization" />
      </div>
      <div class="alert alert-dismissable fade show alert-danger" v-if="applicationError">
        Please provide comprehensive details regarding your planned use of BeamNG.tech (more than 100 characters).
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="form-group">
        <label for="applicationText">Application Text (min. 100 characters):</label>
        <textarea class="form-control" id="applicationText" aria-describedby="applicationHelp" minlength="100" placeholder="Please provide comprehensive details regarding your planned use of BeamNG.tech. Thorough information will enable a quicker and more efficient application review process." v-model.trim="text"></textarea>
      </div>
      <p>Please tick the boxes below to confirm that you agree to comply with the <a target="_blank" href="https://beamng.tech/policies/tos/">Terms of Service</a> and then <a target="_blank" href="https://beamng.tech/policies/privacy-policy/">Privacy Policy</a>.</p>
      <div class="alert alert-dismissable fade show alert-danger" v-if="tosError">
        You need to agree to the <a target="_blank" href="https://beamng.tech/policies/tos/">Terms of Service</a> to apply.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="tos" v-model="tos" />
        <label class="form-check-label" for="tos">I have read and agree to the <a target="_blank" href="https://beamng.tech/policies/tos/">Terms of Service.</a></label>
      </div>
      <div class="alert alert-dismissable fade show alert-danger" v-if="privacyError">
        You need to agree to the <a target="_blank" href="https://beamng.tech/policies/privacy-policy/">Privacy Policy</a> to apply.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="privacy" v-model="privacy" />
        <label class="form-check-label" for="privacy">I have read and agree to the <a target="_blank" href="https://beamng.tech/policies/privacy-policy/">Privacy Policy.</a></label>
      </div>
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="newsletter" v-model="newsletter" />
        <label class="form-check-label" for="newsletter">I want to receive updates about BeamNG.tech. <i>Optional field.</i></label>
      </div>
      <div class="alert alert-dismissable fade show alert-danger" v-if="tokenError">
        Please complete the verification.
        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div>
        <vue-turnstile site-key="0x4AAAAAABCjqrqbVOIu_Cc4" theme="light" @verified="token = $event" />
      </div>
      <hr />
      <button type="submit" class="btn btn-primary">Submit</button>
    </form>
    <div class="alert alert-dismissable fade show alert-primary" v-if="result == 'pending'">
      Your application has been received and is pending approval. You will be emailed about the result. If you are not notified, remember to check your spam folder.
    </div>
    <div class="alert alert-dismissable fade show alert-warn" v-if="result == 'existing'">
      An application using your email already exists. If you think this is an error or simply haven't gotten a response to your existing application yet, <a href="mailto:licensing@beamng.gmbh">contact us.</a>
    </div>
  </div>
</template>

<script>
import VueTurnstile from '@gaviti/vue-turnstile';

export default {
  name: 'RegistrationForm',
  components: { VueTurnstile },
  data: function() {
    return {
      name: '',
      email: '',
      text: '',
      organization: '',
      tos: false,
      privacy: false,
      newsletter: false,

      emailError: false,
      nameError: false,
      applicationError: false,
      tosError: false,
      privacyError: false,
      organizationError: false,
      tokenError: false,
      token: null,
      result: null,
    }
  },
  methods: {
    submit: async function(ev) {
      ev.preventDefault();

      this.tosError = !this.tos;
      this.privacyError = !this.privacy;
      this.nameError = this.name.length === 0;
      this.emailError = this.email.length === 0;
      this.organizationError = this.organization.length === 0;
      this.applicationError = this.text.length < 100;
      this.tokenError = this.token === null || this.token.length === 0;

      if (this.nameError || this.emailError || this.organizationError || this.tosError || this.applicationError || this.privacyError || this.tokenError) {
        return;
      }

      var data = {
        tos: this.tos,
        privacy: this.privacy,
        newsletter: this.newsletter,
        name: this.name,
        email: this.email,
        organization: this.organization,
        text: this.text,
        turnstile: this.token,
      };

      this.axios.put('/s6/v1/researchLicenses', data).then((response) => {
        this.result = response.data.result;
      });
    }
  }
}
</script>

<style scoped>
h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: disc;
  padding: 0 10px;
}
ul > li {
  margin: 0 10px;
}
ol {
  counter-reset: list;
  padding: 0;
}
ol > li {
  list-style: none;
  display: inline-block;
  margin: 0 10px;
}
ol > li:before {
  content: counter(list) ") ";
  counter-increment: list;
  font-weight: bold;
}
a, a:hover {
  color: #FF8800;
}
div.registration p {
  text-align: justify;
}
.alert {
  margin-bottom: 0;
}
</style>
